import { useMemo } from "react";
import { MealPlanningSession } from "@corpuscomplete/core/nutrition/meal-planning-session";
import { queries } from "@corpuscomplete/core/zero";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { MealPlanningMemberContext } from "../member-context";
import { MealPlanningSessionPlanner } from "../session-planner";

export namespace MealPlanningAgentLifecycle {
  export type ActiveSurface =
    | { type: "planner"; sessionId: string }
    | {
        type: "member-context";
        mode: "not-started" | "in-progress" | "complete-waiting";
        sessionId: string | null;
      };

  export function useActiveSurface(mealPlanningSessionId: string): ActiveSurface {
    const plannerSession = MealPlanningSessionPlanner.useSession(mealPlanningSessionId);
    const memberContextSession = MealPlanningMemberContext.useMySession(mealPlanningSessionId);
    const [row] = useQuery(queries.mealPlanningSession.byId({ id: mealPlanningSessionId }));
    const session = row ? MealPlanningSession.asInfo(row) : undefined;
    const memberContextComplete = session?.memberContexts.some(
      (context) => context.userId === memberContextSession?.userId,
    );
    return useMemo(
      () =>
        deriveActiveSurface({
          plannerSessionId: plannerSession?.id,
          memberContextSessionId: memberContextSession?.id,
          memberContextComplete,
        }),
      [plannerSession?.id, memberContextSession?.id, memberContextComplete],
    );
  }

  export function deriveActiveSurface(args: {
    plannerSessionId: string | null | undefined;
    memberContextSessionId: string | null | undefined;
    memberContextComplete: boolean | undefined;
  }): ActiveSurface {
    if (args.plannerSessionId) {
      return { type: "planner", sessionId: args.plannerSessionId };
    }
    if (!args.memberContextSessionId) {
      return { type: "member-context", mode: "not-started", sessionId: null };
    }
    return {
      type: "member-context",
      mode: args.memberContextComplete ? "complete-waiting" : "in-progress",
      sessionId: args.memberContextSessionId,
    };
  }
}
